import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiUsers, FiAward, FiHeart } = FiIcons;

const About = () => {
  const values = [
    { icon: FiShield, title: 'Protection First', description: 'Your business protection is our top priority. We work tirelessly to ensure you have the right coverage for your unique needs.' },
    { icon: FiUsers, title: 'Personal Service', description: 'As a local California agency, we provide personalized service and support that large national companies simply cannot match.' },
    { icon: FiAward, title: 'Expertise', description: 'Our licensed professionals have deep knowledge of California insurance requirements and business protection strategies.' },
    { icon: FiHeart, title: 'Community Focus', description: 'We are committed to supporting California businesses and helping our local economy thrive through proper risk management.' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              About Burbank Insurance Services
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-gray-100 mb-8 max-w-3xl mx-auto"
            >
              Your trusted partner for business insurance in California. Licensed, experienced, and committed to protecting what matters most to your business.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Protecting California Businesses
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Burbank Insurance Services has been serving California businesses with comprehensive insurance solutions. As a licensed insurance agency (CA License #6013543), we understand the unique challenges and requirements facing California businesses.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our partnership with Coterie Insurance allows us to offer cutting-edge business insurance products with the speed and convenience that modern businesses demand, while maintaining the personal touch and local expertise you deserve.
              </p>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <img
                    src="https://app1.sharemyimage.com/2025/05/19/burbank-insurance-services-in-california-logo.png"
                    alt="Burbank Insurance"
                    className="h-12"
                  />
                  <span className="text-2xl font-bold text-blue-600">+</span>
                  <img
                    src="https://cdn1.sharemyimage.com/smi/2025/09/11/Coterie_Logo_Trans_FullColor-1024x185.png"
                    alt="Coterie Insurance"
                    className="h-8"
                  />
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="bg-blue-50 p-8 rounded-xl"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Why Partner with Us?</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiShield} className="h-6 w-6 text-blue-600" />
                  <span className="text-gray-800">Licensed California insurance agency</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiUsers} className="h-6 w-6 text-blue-600" />
                  <span className="text-gray-800">Local expertise and personal service</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiAward} className="h-6 w-6 text-blue-600" />
                  <span className="text-gray-800">Partnership with innovative insurers</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiHeart} className="h-6 w-6 text-blue-600" />
                  <span className="text-gray-800">Committed to your business success</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do at Burbank Insurance Services.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-xl shadow-lg text-center"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Partnership with Coterie Insurance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              We've partnered with Coterie Insurance to bring you innovative business insurance solutions that combine cutting-edge technology with comprehensive coverage.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiShield} className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Modern Technology</h3>
              <p className="text-gray-600">
                Coterie's platform provides instant quotes and streamlined applications, making it easier than ever to get business insurance.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-center"
            >
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiAward} className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Comprehensive Coverage</h3>
              <p className="text-gray-600">
                Access to a full range of business insurance products tailored to meet the specific needs of California businesses.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center"
            >
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiUsers} className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Local Support</h3>
              <p className="text-gray-600">
                While leveraging Coterie's technology, you still get the personal service and local expertise of Burbank Insurance Services.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Work with Us?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Experience the perfect combination of local expertise and innovative insurance solutions. Get started with your business insurance today.
          </p>
          <div className="flex justify-center">
            <a
              href="https://coterie.com/quote"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-orange-600 transition-colors shadow-xl"
            >
              Get My Price
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;